/** @module csi/prefs/_base */
define([
	"dojo/_base/lang",
	"dojo/topic",
	"csi/_base/underscore",
	"csi/access/crews",
	"csi/def/Relationship",
	"csi/locale",
	"csi/sessionConstants",
	"csi/state/rootStore",
	"csi/store/_base",
	"csi/store/Names",
	"csi/xml",
	"csi/xml/Query"
], function(lang, topic, _, crews, Relationship, locale, sessionConstants, {sessionStore}, csiStore, Names, xml, Query) {

	const ATTRIBUTE_CARDS_KEY = "Cards";
	const RESOURCE_PREFIX = "ViewPreferences_";
	const URL_PREFIX = "centric://PREFERENCE/";

	var VC = "^";
	var NC = "~";

	// Boolean: user access levels for custom view changes
	var _bAdmin = null;
	var _bEditor = null;
	var _bPersonalEditor = null;

	// Names: store of parties with which current user can share a custom view
	var _shareStore;

	// reset custom view share store when roles change
	topic.subscribe("/csi/roles/change", function() {
		_shareStore = null;
	});

	/**
	 * Return custom view container URL for custom views used to configure attribute cards
	 * @return {string}
	 */
	function getAttributeCardContainerUrl() {
		return getContainerUrl(ATTRIBUTE_CARDS_KEY);
	}

	/**
	 * Return URL for custom view holding keys for attribute card of given type
	 * @param {string} type
	 * @return {string}
	 */
	function getAttributeCardUrlForType(type) {
		return getAttributeCardContainerUrl() + "/" + type;
	}

	/**
	 * Return custom view container URL for given view key
	 * @param {string} keyView
	 * @return {string}
	 */
	function getContainerUrl(keyView) {
		return URL_PREFIX + keyView;
	}

	/**
	 * Return name for default custom view
	 * @return {string}
	 */
	function getDefaultViewName() {
		return locale.resource("ViewDefault");
	}

	return {
		ATTRIBUTE_CARDS_KEY: ATTRIBUTE_CARDS_KEY,
		ATTR_PREFERENCES: "Preferences",
		ATTR_UMENTRY: "UMEntry",
		RESOURCE_PREFIX: RESOURCE_PREFIX,
		TYPE_CONTAINER: "_CS_PreferenceContainer",
		TYPE_VIEW: "_CS_PreferenceView",
		URL_PREFIX: URL_PREFIX,

		// DEPRECATED: use QueryLevelFilter API rather than use these
		FILTER_CONDITIONS: "C",
		FILTER_FLOATING_DATES: "FD",
		FILTER_LOCALE: "L",
		FILTER_OR: "OR",
		FILTER_CURRENT_USER: "__USER__",

		getAttributeCardContainerUrl,
		getAttributeCardUrlForType,
		getContainerUrl,
		getDefaultViewName,

		composeKey: function(/*String*/idView, /*String?*/idViewContext, /*String?*/idNodeContext) {
			// summary: compose custom view key given view key, view context, and node context
			if (idViewContext) {
				idView += VC + idViewContext;
			}

			if (idNodeContext) {
				idView += NC + idNodeContext;
			}

			return idView;
		},

		decomposeKey: function(/*String*/key) {
			// summary: given custom view key, what other keys (in order of precedence) act as defaults
			var a = key.split(NC);
			var nc = a[1];
			a = a[0].split(VC);
			return {
				view: a[0],
				viewContext: a[1],
				nodeContext: nc
			};
		},

		stripNodeContextFromKey: function(/*String*/key) {
			// summary: strip node context from key when storing in composite view, since they can
			// be used across many node contexts (i.e. Style types)
			return key.split(NC)[0];
		},

		getContainerKey: function(/*String*/urlContainer) {
			// summary: return last part of URL
			return urlContainer.substr(URL_PREFIX.length);
		},

		getShareStore: function() {
			// summary: return store containing list of parties with which a custom view can be shared
			if (!_shareStore) {
				var data = [csiStore.nameObject(locale.resource("SelectAll"))];

				sessionStore.playableRolesStore.internalQuery().forEach(function(item) {
					data.push(_.clone(item));
				});

				data.push(csiStore.nameObject(locale.resource("SelectNone"), sessionConstants.userUrl));

				_shareStore = new Names({
					bSort: false,
					data: data
				});
			}

			return _shareStore;
		},

		getStoreRelationship: function(/*String*/keyView) {
			// summary: return Relationship that can be used to query shared custom views for given view
			var query = new Query();
			query.Not().Node(xml.PROP_TYPE, xml.OP_EQUALS, "User", "Child:UMEntry");

			return lang.mixin(new Relationship(), {
				m_url: getContainerUrl(keyView),
				m_id: this.ATTR_PREFERENCES,
				m_typeNode: this.TYPE_VIEW,
				m_query: query
			});
		},

		hasAdminAccess: function() {
			// summary: determine if you have access to edit shared views
			if (_bAdmin === null) {
				_bAdmin = crews.isInGlobalRoles(["Custom View Administrator"]);
			}

			return _bAdmin;
		},

		hasEditAccess: function() {
			// summary: determine if you are allowed to edit custom views in the context of given node
			if (_bEditor === null) {
				_bEditor = this.hasAdminAccess() || crews.isInGlobalRoles(["Custom View Editor"]);
			}

			return _bEditor;
		},

		/**
		 * Determine if you are allowed to edit personal custom views in the context of given node
		 * @return {boolean}
		 */
		hasPersonalAccess() {
			if (_bPersonalEditor === null) {
				_bPersonalEditor = this.hasEditAccess() || crews.isInGlobalRoles(["Personal Custom View Editor"]);
			}

			return _bPersonalEditor;
		},

		normalizeFilterCondition: function(/*Object*/obj) {
			// summary: make sure filter condition is completely filled in so compare method does not return false positives
			obj.group = !!obj.group;
			obj.key = obj.key || "";
			obj.op = obj.op || "";
			obj.relative = !!obj.relative;
			obj.value = (obj.value == undefined) ? null : obj.value;
			return obj;
		},

		optionResource: function(/*String*/suffix, /*Any[]?*/params) {
			// summary: return ViewPreferences resource
			return locale.resource(RESOURCE_PREFIX + suffix, params);
		}
	};
});
